import React, { useState } from 'react';
import { Form, Formik, useFormikContext } from 'formik';
import * as Yup from 'yup';

import { useStyles } from '~/assets/styles';
import CardDialog from '~/components/CardDialog';
import Button from '~/components/core/Atomic/Buttons/Button';
import IconButton from '~/components/core/Atomic/Buttons/IconButton';
import InputAdornment from '~/components/core/Atomic/InputAdornment';
import CancelButton from '~/components/core/Buttons/CancelButton';
import SwitchFormik from '~/components/core/Formik/SwitchFormik';
import { EyeIcon, EyeSlashIcon } from '~/components/icons';
import TextFieldFormik from '~/components/TextFieldFormik';

import TEXTS from './texts';
import type { GbgConfigurationFormInitialValues, GbgConfigurationFormValues } from './types';

interface GbgSetupFormProps {
  isEdit: boolean;
  onClose: () => void;
}

interface GbgSetupDialogProps extends GbgSetupFormProps {
  onSubmit: (values: GbgConfigurationFormValues) => Promise<void>;
  initialValues?: GbgConfigurationFormInitialValues;
}

const GbgSetupDialog: React.FC<GbgSetupDialogProps> = ({ onSubmit, onClose, isEdit = false, initialValues }) => {
  const validationSchema = Yup.object().shape({
    api_base_url: Yup.string().trim().required(TEXTS.required),
    api_username: Yup.string().trim().required(TEXTS.required),
    api_token: isEdit ? Yup.string().trim().nullable() : Yup.string().trim().required(TEXTS.required),
    is_address_validation_enabled: Yup.boolean(),
    is_business_validation_enabled: Yup.boolean(),
  });

  return (
    <Formik<GbgConfigurationFormValues>
      initialValues={{
        api_base_url: initialValues?.api_base_url || '',
        api_username: initialValues?.api_username || '',
        api_token: '',
        is_address_validation_enabled: initialValues?.is_address_validation_enabled || false,
        is_business_validation_enabled: initialValues?.is_business_validation_enabled || false,
      }}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
    >
      <GbgSetupForm isEdit={isEdit} onClose={onClose} />
    </Formik>
  );
};

const GbgSetupForm = ({ isEdit, onClose }: GbgSetupFormProps) => {
  const { isSubmitting } = useFormikContext();
  const { classes } = useStyles();

  return (
    <CardDialog
      title={isEdit ? TEXTS.editConfiguration : TEXTS.createConfiguration}
      isDialog
      preventClose={isSubmitting}
      maxWidth="sm"
      fullWidth
      onClose={onClose}
    >
      <Form>
        <div className="grid gap-20">
          <TextFieldFormik
            id="api_base_url"
            label={TEXTS.formLabels.apiBaseUrl}
            className={classes.textField}
            fullWidth
          />
          <TextFieldFormik
            id="api_username"
            label={TEXTS.formLabels.apiUsername}
            className={classes.textField}
            fullWidth
          />
          <ApiTokenField isEdit={isEdit} />
          <SwitchFormik
            id="is_address_validation_enabled"
            label={TEXTS.formLabels.isAddressValidationEnabled}
            hideErrorGap
          />
          <SwitchFormik
            id="is_business_validation_enabled"
            label={TEXTS.formLabels.isBusinessValidationEnabled}
            hideErrorGap
          />
        </div>
        <div className="mt-20 flex w-full justify-end">
          <CancelButton disabled={isSubmitting} onClick={onClose} />
          <Button variant="contained" color="primary" disabled={isSubmitting} type="submit">
            {TEXTS.save}
          </Button>
        </div>
      </Form>
    </CardDialog>
  );
};

const ApiTokenField = ({ isEdit }: { isEdit: boolean }) => {
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  return (
    <TextFieldFormik
      id="api_token"
      label={isEdit ? TEXTS.formLabels.apiTokenEdit : TEXTS.formLabels.apiToken}
      InputProps={{
        type: isPasswordVisible ? 'text' : 'password',
        endAdornment: (
          <InputAdornment position="end">
            <IconButton onClick={() => setIsPasswordVisible(!isPasswordVisible)}>
              {isPasswordVisible ? <EyeIcon iconColor="currentColor" /> : <EyeSlashIcon iconColor="currentColor" />}
            </IconButton>
          </InputAdornment>
        ),
      }}
    />
  );
};

export default GbgSetupDialog;
